import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { INavMenuProps } from './types'
import styles from './styles.module.scss'
import { offset, useFloating } from '@floating-ui/react'

type MouseEventWithElement<T extends HTMLElement> = React.MouseEvent<T, MouseEvent>

type SubmenuHeights = {
  [key: string]: number
}

const NavMenu = ({
  items,
  label = 'Navigation Menu',
  size = 'md',
  className,
  isCollapsed = false,
  style,
  canOpenMultiple = true,
  hasAnimation = false,
  ...props
}: INavMenuProps) => {
  // State to store the open submenus
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({})
  // Flag to check if it's the first render, so we can avoid updating the submenu heights on the first render
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)
  // State to store the submenu heights
  const [submenuHeights, setSubmenuHeights] = useState<SubmenuHeights>({})
  // Use useMemo to memoize the initialization of submenuRefs
  const submenuRefs: Record<number, React.MutableRefObject<HTMLDivElement | null>> = useMemo(
    () => ({}),
    [],
  )
  // Toggle the submenu when the user clicks on the button, and close all others if openMultiple is false
  const handleToggleSubmenu = (id: number) => {
    if (isCollapsed || !canOpenMultiple) {
      setOpenSubmenus((prevOpenSubmenus) => ({
        [id]: !prevOpenSubmenus[id],
      }))
    } else {
      setOpenSubmenus((prevOpenSubmenus) => ({
        ...prevOpenSubmenus,
        [id]: !prevOpenSubmenus[id],
      }))
    }
  }
  // on hover, toggle the submenu when collapsed
  const handleHover = (id: number) => {
    if (isCollapsed) {
      handleToggleSubmenu(id)
    }
  }
  // on tap, toggle the submenu and close all others
  const handleTouch = (_: React.TouchEvent, id: number) => {
    if (isCollapsed) {
      setOpenSubmenus((prevOpenSubmenus) => ({
        [id]: !prevOpenSubmenus[id],
      }))
    }
  }
  // Close all submenus when the user hovers out of the menu when collapsed
  const handleHoverLeave = () => {
    if (isCollapsed) {
      setOpenSubmenus({})
    }
  }
  // Floating menu configuration
  const { refs, floatingStyles } = useFloating({
    placement: props.dropdownPlacement ? props.dropdownPlacement : 'top-end',
    strategy: props.dropdownStrategy ? props.dropdownStrategy : 'absolute',
    transform: props.dropdownTransform ? props.dropdownTransform : false,
    middleware: [
      offset(({ rects }) => ({
        alignmentAxis: -rects.floating.width - (props.dropdownOffsetX || 20),
        mainAxis: -44 - (props.dropdownOffsetY || 0),
      })),
    ],
  })
  // checks if the element is an anchor element and returns a boolean
  const isAnchorElement = (element: HTMLElement): element is HTMLAnchorElement => {
    return element.tagName.toLowerCase() === 'a'
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleGenericClick = <T extends HTMLElement>(e: MouseEventWithElement<T>, tab: any) => {
    // Your logic here
    if (isAnchorElement(e.currentTarget)) {
      tab.onClick && tab.onClick(e as unknown as React.MouseEvent<HTMLAnchorElement>)
    }
  }
  // check if any of the top level items have openByDefault set to true - if so, add them to the openSubmenus list. This is handy for having menus open on when the user is on one of the subpages of that menu
  useEffect(() => {
    if (!isFirstRender) {
      return
    }
    const newState = {} as Record<string, boolean>

    Object.keys(submenuRefs).forEach((key) => {
      const index = parseInt(key, 10)
      // Type assertion to let TypeScript know that items[index] has the necessary properties
      const menuItem = items[index] as { variant: string; isOpenByDefault?: boolean }
      newState[index] =
        menuItem.variant === 'dropdown' && menuItem.isOpenByDefault === true ? true : false
    })

    setOpenSubmenus(newState)

    // Set the flag to false after the first render
    setIsFirstRender(false)
  }, [isFirstRender, items, submenuRefs])

  // Update submenu heights when they are opened
  useEffect(() => {
    // Update submenu heights when they are opened
    Object.keys(openSubmenus).forEach(async (id) => {
      const currentRef = submenuRefs[Number(id)]?.current
      if (openSubmenus[id] && currentRef) {
        await new Promise(requestAnimationFrame)
        const newHeight = currentRef.scrollHeight
        setSubmenuHeights((prevHeights) => ({
          ...prevHeights,
          [id]: newHeight,
        }))
      }
    })
    // Set the flag to false after the first render
    setIsFirstRender(false)
  }, [openSubmenus, submenuRefs, isCollapsed])

  // User can only open one dropdown at a time when collapsed by hovering/tapping - so if the user has more than one menu open, it will bug, so we close them all when the menu is collapsed
  useEffect(() => {
    if (!isCollapsed) {
      return
    }
    setOpenSubmenus({})
  }, [isCollapsed])

  return (
    <nav
      className={clsx('mui-navigation-menu', styles.navigationMenu, styles[size], className, {
        [styles.collapsed]: isCollapsed,
      })}
      data-testid="mui-navigation-menu"
      style={{ ...style }}
      {...props}
    >
      <ul
        aria-label={label}
        className={`${styles.navigationMenuList} ${styles[size]}`}
        role="tablist"
      >
        {items.map((tab, index) => {
          return (
            <li
              key={`${tab.id.replace(' ', '').toLowerCase()}-tab`}
              ref={refs.setReference}
              onMouseEnter={() => handleHover(index)}
              onMouseLeave={() => handleHoverLeave()}
              onTouchEnd={(e) => handleTouch(e, index)}
            >
              {tab.variant === 'button' || tab.variant === 'dropdown' ? (
                <div>
                  <button
                    aria-controls={`${tab.id}-tab`}
                    className={clsx(styles.button, styles[size], tab.className, {
                      [styles.disabled]: tab.isDisabled,
                      [styles.isOpen]: openSubmenus[index],
                      [styles.currentPage]: tab.isCurrent,
                      [styles.collapsed]: isCollapsed,
                    })}
                    id={tab.id}
                    role="tab"
                    onClick={(e) => {
                      if (tab.variant === 'dropdown') {
                        handleToggleSubmenu(index)
                        tab.onClick && tab.onClick(e)
                      }

                      if (tab.variant === 'button') {
                        tab.onClick(e)
                      }
                    }}
                  >
                    <span className={clsx(styles.label, { [styles.collapsed]: isCollapsed })}>
                      {tab.icon && <span className={styles.icon}>{tab.icon}</span>}
                      {!isCollapsed && tab.label}
                      {tab.trailingIcon && (
                        <span className={styles.trailingIcon}>{tab.trailingIcon}</span>
                      )}
                    </span>
                    {tab.hasBadge && <span aria-hidden={true} className={styles.badge}></span>}

                    {tab.variant === 'dropdown' && !isCollapsed ? (
                      tab.hideChevron && tab.hideChevron === true ? (
                        <></>
                      ) : (
                        <span className={styles.chevron}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="16"
                            width="16"
                            viewBox="0 0 512 512"
                          >
                            <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
                          </svg>
                        </span>
                      )
                    ) : (
                      <></>
                    )}
                  </button>
                  {tab.variant === 'dropdown' && tab.subItems.length > 0 ? (
                    <nav
                      className={clsx(styles.subMenu, styles[size], {
                        [styles.collapsed]: isCollapsed,
                        [styles.hasAnimation]: hasAnimation,
                      })}
                      ref={
                        isCollapsed
                          ? refs.setFloating
                          : (ref) =>
                              (submenuRefs[index] = { current: ref as HTMLDivElement | null })
                      }
                      style={
                        isCollapsed
                          ? floatingStyles
                          : {
                              height:
                                openSubmenus[index] && !isFirstRender
                                  ? `${submenuHeights[index]}px`
                                  : '0',
                            }
                      }
                    >
                      <ul
                        className={clsx(styles.ul, styles[size], {
                          [styles.collapsed]: isCollapsed,
                        })}
                      >
                        {isCollapsed && <h4 className={styles.dropdownTitle}>{tab.label}</h4>}
                        {tab.subItems.map((subItem, index) => (
                          <li key={`${index}-${subItem.id}-subtab`}>
                            {subItem.variant === 'button' ? (
                              <button
                                type="button"
                                aria-controls={`${subItem.id}-subtab`}
                                aria-selected={`${subItem.isCurrent ? true : false}`}
                                aria-current={`${subItem.isCurrent ? 'page' : false}`}
                                className={clsx(styles.button, styles[size], subItem.className, {
                                  [styles.disabled]: subItem.isDisabled,
                                  [styles.currentSubPage]: subItem.isCurrent,
                                  [styles.collapsed]: isCollapsed,
                                })}
                                id={`${subItem.id}`}
                                onClick={(e) => subItem.onClick(e)}
                                role="tab"
                              >
                                <span
                                  className={clsx(styles.label, {
                                    [styles.collapsed]: isCollapsed,
                                  })}
                                >
                                  {subItem.icon && (
                                    <span className={styles.icon}>{subItem.icon}</span>
                                  )}
                                  {subItem.label}
                                  {subItem.trailingIcon && (
                                    <span className={styles.trailingIcon}>
                                      {subItem.trailingIcon}
                                    </span>
                                  )}
                                </span>
                              </button>
                            ) : (
                              <a
                                aria-controls={`${subItem.id}-subtab`}
                                aria-selected={`${subItem.isCurrent ? true : false}`}
                                aria-current={`${subItem.isCurrent ? 'page' : false}`}
                                className={clsx(styles[size], subItem.className, {
                                  [styles.disabled]: subItem.isDisabled,
                                  [styles.currentSubPage]: subItem.isCurrent,
                                  [styles.collapsed]: isCollapsed,
                                })}
                                id={`${subItem.id}`}
                                href={subItem.href}
                                role="tab"
                                target={subItem.target ? subItem.target : '_self'}
                                onClick={(e) => handleGenericClick<HTMLAnchorElement>(e, subItem)}
                              >
                                <span
                                  className={clsx(styles.label, {
                                    [styles.collapsed]: isCollapsed,
                                  })}
                                >
                                  {subItem.icon && (
                                    <span className={styles.icon}>{subItem.icon}</span>
                                  )}
                                  {subItem.label}{' '}
                                  {subItem.trailingIcon && (
                                    <span className={styles.trailingIcon}>
                                      {subItem.trailingIcon}
                                    </span>
                                  )}
                                </span>
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div
                        className={styles.subBlocker}
                        style={{ paddingLeft: `${props.dropdownOffsetX || 20}px` }}
                      ></div>
                    </nav>
                  ) : (
                    <div
                      className={clsx(styles.subMenu, styles[size], {
                        [styles.collapsed]: isCollapsed,
                      })}
                      ref={
                        isCollapsed
                          ? refs.setFloating
                          : (ref) =>
                              (submenuRefs[index] = { current: ref as HTMLDivElement | null })
                      }
                      style={
                        isCollapsed
                          ? floatingStyles
                          : {
                              height:
                                openSubmenus[index] && !isFirstRender
                                  ? `${submenuHeights[index]}px`
                                  : '0',
                            }
                      }
                    >
                      <div
                        className={clsx(styles.innerHolder, { [styles.collapsed]: isCollapsed })}
                      >
                        <h4 className={styles.dropdownTitle}>{tab.label}</h4>
                      </div>
                    </div>
                  )}
                </div>
              ) : tab.isCurrent ? (
                <>
                  <span
                    aria-controls={`${tab.id}-tab`}
                    aria-selected={`${tab.isCurrent ? true : false}`}
                    aria-current={`${tab.isCurrent ? 'page' : false}`}
                    className={clsx(
                      styles.listItem,
                      styles[size],
                      styles.currentPage,
                      tab.className,
                      {
                        [styles.disabled]: tab.isDisabled,
                        [styles.isOpen]: openSubmenus[index],
                        [styles.collapsed]: isCollapsed,
                      },
                    )}
                    id={tab.id}
                    role="tab"
                  >
                    <span className={clsx(styles.label, { [styles.collapsed]: isCollapsed })}>
                      {tab.icon && <span className={styles.icon}>{tab.icon}</span>}
                      {!isCollapsed && tab.label}
                    </span>
                    {tab.hasBadge && <span aria-hidden={true} className={styles.badge}></span>}
                  </span>
                  <div
                    className={clsx(styles.subMenu, styles[size], {
                      [styles.collapsed]: isCollapsed,
                    })}
                    ref={
                      isCollapsed
                        ? refs.setFloating
                        : (ref) => (submenuRefs[index] = { current: ref as HTMLDivElement | null })
                    }
                    style={
                      isCollapsed
                        ? floatingStyles
                        : {
                            height:
                              openSubmenus[index] && !isFirstRender
                                ? `${submenuHeights[index]}px`
                                : '0',
                          }
                    }
                  >
                    <div className={clsx(styles.innerHolder, { [styles.collapsed]: isCollapsed })}>
                      <h4 className={styles.dropdownTitle}>{tab.label}</h4>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <a
                    aria-controls={`${tab.id}-tab`}
                    aria-selected={`${tab.isCurrent ? true : false}`}
                    aria-current={`${tab.isCurrent ? 'page' : false}`}
                    className={clsx(styles[size], tab.className, {
                      [styles.disabled]: tab.isDisabled,
                      [styles.isOpen]: openSubmenus[index],
                      [styles.collapsed]: isCollapsed,
                    })}
                    id={tab.id}
                    href={tab.href}
                    role="tab"
                    target={tab.target ? tab.target : '_self'}
                    onClick={(e) => handleGenericClick<HTMLAnchorElement>(e, tab)}
                  >
                    <span className={clsx(styles.label, { [styles.collapsed]: isCollapsed })}>
                      {tab.icon && <span className={styles.icon}>{tab.icon}</span>}
                      {!isCollapsed && tab.label}
                    </span>
                    {tab.hasBadge && <span aria-hidden={true} className={styles.badge}></span>}
                  </a>
                  <div
                    className={clsx(styles.subMenu, styles[size], {
                      [styles.collapsed]: isCollapsed,
                    })}
                    ref={
                      isCollapsed
                        ? refs.setFloating
                        : (ref) => (submenuRefs[index] = { current: ref as HTMLDivElement | null })
                    }
                    style={
                      isCollapsed
                        ? floatingStyles
                        : {
                            height:
                              openSubmenus[index] && !isFirstRender
                                ? `${submenuHeights[index]}px`
                                : '0',
                          }
                    }
                  >
                    <div className={clsx(styles.innerHolder, { [styles.collapsed]: isCollapsed })}>
                      <h4 className={styles.dropdownTitle}>{tab.label}</h4>
                    </div>
                  </div>
                </>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default NavMenu
